import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { PublicClientApplication, EventType, type EventMessage, type AuthenticationResult } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { msalConfig } from './lib/msalConfig';
import { setMsalInstance, setActiveAccount } from './lib/apiClient';
import { queryClient } from './lib/queryClient';
import App from './App';

async function bootstrap() {
  const msalInstance = new PublicClientApplication(msalConfig);

  await msalInstance.initialize();

  // Set active account from cache on load
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);
    setActiveAccount(accounts[0]);
  }

  // Keep active account in sync when login/logout events fire
  msalInstance.addEventCallback((event: EventMessage) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload as AuthenticationResult;
      msalInstance.setActiveAccount(payload.account);
      setActiveAccount(payload.account);
    }
    if (event.eventType === EventType.LOGOUT_SUCCESS) {
      setActiveAccount(null);
    }
  });

  setMsalInstance(msalInstance);

  const root = document.getElementById('root');
  if (!root) throw new Error('Root element not found');

  createRoot(root).render(
    <StrictMode>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </MsalProvider>
    </StrictMode>,
  );
}

// Fire a warm-up ping so the App Service is awake before the user hits a data page.
// The health endpoint requires no auth, so this runs immediately on page load.
const _apiRoot = ((import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api/v1')
  .replace(/\/api\/v\d+\/?$/, '');
fetch(`${_apiRoot}/health`).catch(() => {});

bootstrap();
